import type { ValidatieResultaat } from "../api";

type Props = {
  validaties: ValidatieResultaat[];
};

/**
 * Vierkants-controle paneel: visuele samenvatting van alle validatie-regels.
 * Groen vinkje per ok-rule, oranje waarschuwing per afwijking (met verschil), grijs
 * voor niet-berekenbare rules (ontbrekend veld etc).
 */
export function ValidationPanel({ validaties }: Props) {
  if (!validaties || validaties.length === 0) return null;

  const ok = validaties.filter((v) => v.status === "ok").length;
  const afw = validaties.filter((v) => v.status === "afwijking").length;
  const nb = validaties.filter((v) => v.status === "niet-berekenbaar").length;

  const headerBg =
    afw > 0 ? "bg-amber-50 border-amber-200"
      : nb > 0 && ok === 0 ? "bg-gray-50 border-gray-200"
      : "bg-emerald-50 border-emerald-200";
  const headerText =
    afw > 0 ? "text-amber-900" : nb > 0 && ok === 0 ? "text-gray-700" : "text-emerald-900";

  return (
    <section className={`border rounded-lg overflow-hidden ${headerBg}`}>
      <header className="px-5 py-3 border-b border-current/10 flex items-center justify-between">
        <h2 className={`font-semibold ${headerText}`}>Vierkants-controle</h2>
        <div className="flex items-center gap-3 text-xs">
          {ok > 0 && <span className="text-emerald-700">✓ {ok} ok</span>}
          {afw > 0 && <span className="text-amber-700 font-medium">⚠ {afw} afwijking</span>}
          {nb > 0 && <span className="text-gray-500">— {nb} niet berekenbaar</span>}
        </div>
      </header>
      <ul className="divide-y bg-white">
        {validaties.map((v, i) => (
          <RuleRow key={i} v={v} />
        ))}
      </ul>
    </section>
  );
}

function RuleRow({ v }: { v: ValidatieResultaat }) {
  const icon = v.status === "ok" ? "✓" : v.status === "afwijking" ? "⚠" : "—";
  const iconClass =
    v.status === "ok"
      ? "text-emerald-600"
      : v.status === "afwijking"
      ? "text-amber-600"
      : "text-gray-400";
  const bgClass =
    v.status === "afwijking" ? "bg-amber-50" : "";

  return (
    <li className={`px-5 py-3 ${bgClass}`}>
      <div className="flex items-start gap-3">
        <span className={`text-xl leading-none ${iconClass}`}>{icon}</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900">
            {v.omschrijving || v.formule}
          </div>
          <div className="text-xs text-gray-500 font-mono mt-0.5 truncate">{v.formule}</div>
          {v.status === "afwijking" && (
            <div className="mt-1 text-xs text-amber-800">
              Gevonden <span className="font-mono font-medium">{fmt(v.links)}</span>, verwacht{" "}
              <span className="font-mono font-medium">{fmt(v.rechts)}</span> — verschil{" "}
              <span className="font-mono font-medium">{fmt(v.verschil)}</span>
              {v.tolerantie != null && (
                <span className="text-gray-500"> (tolerantie {fmt(v.tolerantie)})</span>
              )}
            </div>
          )}
          {v.status === "niet-berekenbaar" && v.reden && (
            <div className="mt-1 text-xs text-gray-600">{v.reden}</div>
          )}
        </div>
      </div>
    </li>
  );
}

function fmt(n: number | null | undefined): string {
  if (n == null) return "–";
  return n.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
